// src/components/RatingPrompt.jsx

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, ThumbsUp, MessageSquare, X, Loader } from 'lucide-react';
import { doc, setDoc, increment } from 'firebase/firestore'; // ✅ ADDED increment
import { db } from '../services/firebaseConfig';

const RatingPrompt = ({ 
  restaurantId, 
  tableId, 
  orderId,
  restaurantName,
  googlePlaceId,
  onClose 
}) => {
  const [step, setStep] = useState('rating');
  const [selectedRating, setSelectedRating] = useState(0);
  const [feedbackText, setFeedbackText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleRatingSelect = async (rating) => {
    setSelectedRating(rating);
    await saveInAppRating(rating);
    
    if (rating >= 4) {
      setStep('redirect');
    } else {
      setStep('feedback');
    }
  };

  const saveInAppRating = async (rating) => {
    try {
      const ratingRef = doc(
        db, 
        'restaurants', 
        restaurantId, 
        'ratings', 
        `${orderId}_${Date.now()}`
      );
      
      await setDoc(ratingRef, {
        orderId,
        tableId: tableId || 'walk-in',
        rating,
        timestamp: new Date().toISOString(),
        restaurantId,
        source: 'in-app'
      });
      
      console.log('✅ In-app rating saved:', rating);
    } catch (error) {
      console.error('❌ Error saving rating:', error);
    }
  };

  const handleGoogleRedirect = () => {
    if (!googlePlaceId) {
      alert('Google Place ID not configured');
      return;
    }

    const reviewUrl = `https://search.google.com/local/writereview?placeid=${googlePlaceId}`;
    trackGoogleRedirect();
    window.open(reviewUrl, '_blank', 'noopener,noreferrer');
    
    setTimeout(() => {
      onClose();
    }, 1000);
  };

  const trackGoogleRedirect = async () => {
    try {
      const analyticsRef = doc(
        db,
        'restaurants',
        restaurantId,
        'analytics',
        'google_redirects'
      );
      
      await setDoc(analyticsRef, {
        totalRedirects: increment(1),
        lastRedirect: new Date().toISOString(),
        [`rating_${selectedRating}`]: increment(1)
      }, { merge: true });
      
      console.log('✅ Google redirect tracked');
    } catch (error) {
      console.error('❌ Error tracking redirect:', error);
    }
  };

  const handleSubmitFeedback = async () => {
    if (!feedbackText.trim()) {
      alert('Please enter your feedback');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const feedbackRef = doc(
        db,
        'restaurants',
        restaurantId,
        'feedback',
        `${orderId}_${Date.now()}`
      );
      
      await setDoc(feedbackRef, {
        orderId,
        tableId: tableId || 'walk-in',
        rating: selectedRating,
        feedback: feedbackText,
        timestamp: new Date().toISOString(),
        status: 'unread'
      });
      
      alert('Thank you for your feedback! We will work on improving.');
      onClose();
    } catch (error) {
      console.error('❌ Error submitting feedback:', error);
      alert('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.9, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-3xl p-6 md:p-8 max-w-md w-full shadow-2xl relative"
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition"
          >
            <X className="w-6 h-6" />
          </button>

          {step === 'rating' && (
            <div className="text-center">
              <motion.div
                animate={{ rotate: [0, 10, -10, 0] }}
                transition={{ duration: 0.5, repeat: 2 }}
                className="text-6xl mb-4"
              >
                🍽️
              </motion.div>
              
              <h2 className="text-2xl font-bold text-gray-800 mb-2">
                How was your experience?
              </h2>
              <p className="text-gray-600 text-sm mb-8">
                at {restaurantName}
              </p>

              <div className="grid grid-cols-5 gap-2 mb-6">
                {[
                  { emoji: '😍', value: 5, label: 'Loved it!' },
                  { emoji: '😊', value: 4, label: 'Great' },
                  { emoji: '😐', value: 3, label: 'Okay' },
                  { emoji: '😟', value: 2, label: 'Not good' },
                  { emoji: '😢', value: 1, label: 'Poor' }
                ].map((item) => (
                  <motion.button
                    key={item.value}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleRatingSelect(item.value)}
                    className="flex flex-col items-center p-2 md:p-3 rounded-2xl bg-gray-50 hover:bg-orange-50 transition-all"
                  >
                    <span className="text-3xl md:text-4xl mb-1">{item.emoji}</span>
                    <span className="text-xs text-gray-600 font-medium">
                      {item.label}
                    </span>
                  </motion.button>
                ))}
              </div>

              <div className="border-t pt-6">
                <p className="text-xs text-gray-500 mb-3">Or rate with stars</p>
                <div className="flex justify-center gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <motion.button
                      key={star}
                      whileHover={{ scale: 1.2 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => handleRatingSelect(star)}
                    >
                      <Star
                        className={`w-8 h-8 md:w-10 md:h-10 transition-all ${
                          star <= selectedRating
                            ? 'text-yellow-400 fill-yellow-400'
                            : 'text-gray-300 hover:text-yellow-200'
                        }`}
                      />
                    </motion.button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 'redirect' && (
            <div className="text-center">
              <motion.div
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 0.5, repeat: 2 }}
                className="text-6xl mb-4"
              >
                ⭐
              </motion.div>
              
              <h2 className="text-2xl font-bold text-gray-800 mb-2">
                Thank you! 🎉
              </h2>
              <p className="text-gray-600 mb-6">
                We're glad you enjoyed your experience!
              </p>

              <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-2xl p-6 mb-6 border-2 border-orange-200">
                <ThumbsUp className="w-12 h-12 text-orange-500 mx-auto mb-3" />
                <p className="text-gray-700 font-semibold mb-2">
                  Help us grow! 🌱
                </p>
                <p className="text-sm text-gray-600 mb-4">
                  Share your experience on Google and help other food lovers discover {restaurantName}
                </p>
                
                <button
                  onClick={handleGoogleRedirect}
                  className="w-full bg-gradient-to-r from-orange-500 to-red-500 text-white py-3 md:py-4 rounded-xl font-bold text-base md:text-lg hover:shadow-lg transition-all flex items-center justify-center gap-2"
                >
                  <img 
                    src="https://www.google.com/favicon.ico" 
                    alt="Google"
                    className="w-5 h-5 md:w-6 md:h-6"
                  />
                  Write Google Review
                </button>
                
                <p className="text-xs text-gray-500 mt-3">
                  Takes just 30 seconds • Opens in Google Maps
                </p>
              </div>

              <button
                onClick={onClose}
                className="text-gray-500 text-sm hover:text-gray-700 transition"
              >
                Maybe later
              </button>
            </div>
          )}

          {step === 'feedback' && (
            <div>
              <div className="text-center mb-6">
                <span className="text-6xl mb-4 block">😔</span>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">
                  We're sorry to hear that
                </h2>
                <p className="text-gray-600 text-sm">
                  Your feedback helps us improve. Please tell us what went wrong:
                </p>
              </div>

              <textarea
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
                placeholder="What could we have done better?"
                className="w-full h-32 border-2 border-gray-200 rounded-xl p-4 mb-4 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none resize-none"
                autoFocus
              />

              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-xl font-semibold hover:bg-gray-300 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitFeedback}
                  disabled={isSubmitting}
                  className="flex-1 bg-orange-500 text-white py-3 rounded-xl font-semibold hover:bg-orange-600 transition disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <Loader className="w-5 h-5 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <MessageSquare className="w-5 h-5" />
                      Submit Feedback
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default RatingPrompt;